import { ensureSafeComponent } from '@embroider/util';
import VerticalCollection from '@html-next/vertical-collection/components/vertical-collection/component';
import InfinityLoader from 'ember-infinity/components/infinity-loader';
import EmberPowerSelectOptions from 'ember-power-select/components/power-select/options';
import emberPowerSelectIsEqual from 'ember-power-select/helpers/ember-power-select-is-equal';
import emberPowerSelectIsGroup from 'ember-power-select/helpers/ember-power-select-is-group';
import { and } from 'ember-truth-helpers';
import BvSpinner from 'volta/components/bv-spinner';
import orElse from 'volta/helpers/or-else';

import type { ComponentLike, WithBoundArgs } from '@glint/template';
import type InfinityModel from 'ember-infinity/lib/infinity-model';
import type { Select } from 'ember-power-select/components/power-select';

type BvSelectOptionsArgs = Omit<EmberPowerSelectOptions['args'], 'options' | 'listboxId'> & {
  infiniteScroll?: boolean;
  infiniteModel?: InfinityModel;
  renderAll?: boolean;
  optionHeight?: number;
  bufferSize?: number;
  idForFirstItem?: string;
  optionKey?: string;
  isLoading?: boolean;
  options?: any[] | PromiseProxy<any[]> | InfinityModel;
};

export interface BvSelectOptionsSignature {
  Element: HTMLElement;
  Args: BvSelectOptionsArgs;
  Blocks: {
    default: [unknown, Select];
  };
}

type BvSelectionOptionsForRegistry = WithBoundArgs<
  ComponentLike<BvSelectOptionsSignature>,
  'select' | 'loadingMessage' | 'groupIndex'
>;

export default class BvSelectOptionsComponent extends EmberPowerSelectOptions {
  <template>
    {{! @glint-nocheck -- Subclassing is bad}}
    {{#let (and @infiniteScroll @infiniteModel) as |showLoader|}}
      <ul role='listbox' {{this.setupHandlers}} ...attributes>
        {{#if (and @select.loading @loadingMessage)}}
          <li
            class='ember-power-select-option ember-power-select-option--loading-message'
            role='option'
            aria-selected={{false}}
          >
            {{@loadingMessage}}
          </li>
        {{/if}}
        <VerticalCollection
          @items={{@options}}
          @estimateHeight={{orElse @optionHeight 28}}
          @renderAll={{@renderAll}}
          @bufferSize={{orElse @bufferSize 3}}
          @idForFirstItem={{@idForFirstItem}}
          @key={{orElse @optionKey '@identity'}}
          as |opt index|
        >
          {{#if (and (emberPowerSelectIsGroup opt))}}
            {{#let
              (if @groupComponent (component (ensureSafeComponent @groupComponent this)))
              (if @optionsComponent (component (ensureSafeComponent @optionsComponent this)))
              as |Group Options|
            }}
              <Group @group={{opt}} @select={{@select}} @extra={{@extra}}>
                <Options
                  @options={{opt.options}}
                  @select={{@select}}
                  @groupIndex='{{@groupIndex}}{{index}}.'
                  @optionsComponent={{@optionsComponent}}
                  @groupComponent={{@groupComponent}}
                  @extra={{@extra}}
                  role='group'
                  data-optgroup='true'
                  class='ember-power-select-options'
                  as |option|
                >
                  {{yield option @select}}
                </Options>
              </Group>
            {{/let}}
          {{else}}
            <li
              class='ember-power-select-option'
              id='{{@select.uniqueId}}-{{@groupIndex}}{{index}}'
              aria-selected='{{emberPowerSelectIsEqual opt @select.selected}}'
              aria-disabled={{if opt.disabled 'true'}}
              aria-current='{{emberPowerSelectIsEqual opt @select.highlighted}}'
              data-option-index='{{@groupIndex}}{{index}}'
            >
              {{yield opt @select}}
            </li>
          {{/if}}
        </VerticalCollection>

        {{#if showLoader}}
          <li>
            <InfinityLoader @infinityModel={{@infiniteModel}} @hideOnInfinity={{true}}>
              <BvSpinner @size='xs' />
            </InfinityLoader>
          </li>
        {{/if}}
      </ul>
    {{/let}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'BvSelect::Options': BvSelectionOptionsForRegistry;
    'bv-select/options': BvSelectionOptionsForRegistry;
  }
}
